import { createContext, useContext, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { whitelabelService } from '../services/whitelabelService'
import { localStorageKeys } from '../config/localStorageKeys'
import { apiFGTS, httpClient } from '../services/http/httpClient'
import {
  AppConfigResponse,
  getAppConfig,
} from '../services/whitelabelService/appConfig'

export type Withelabel = {
  code: string
  url: string
}

interface WhitelabelContextProps {
  login: (code: string) => Promise<void>
  logout: () => void
  whitelabel: Withelabel | null
  appConfig?: AppConfigResponse
  showCodeScreen: boolean
}

const WhitelabelContext = createContext<WhitelabelContextProps>(
  {} as WhitelabelContextProps,
)
const showCodeScreen = import.meta.env.VITE_CODE_SCREEN === 'true'

export const WhitelabelProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { mutateAsync } = useMutation({
    mutationFn: (code: string) => whitelabelService.Login({ code }),
  })

  const [whitelabel, setWhitelabel] = useState(() => {
    const res = localStorage.getItem(localStorageKeys.WHITELABEL)
    if (!res) return null

    return JSON.parse(res)
  })

  const login = async (code: string) => {
    try {
      const data = await mutateAsync(code)

      setWhitelabel({ code, url: data.url })
      localStorage.setItem(
        localStorageKeys.WHITELABEL,
        JSON.stringify({
          code,
          url: data.url,
        }),
      )
      httpClient.defaults.baseURL = data.url
      apiFGTS.defaults.baseURL = data.url
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const logout = () => {
    setWhitelabel(null)
    localStorage.setItem(localStorageKeys.WHITELABEL, '')

    httpClient.defaults.baseURL = import.meta.env.VITE_API_URL
    apiFGTS.defaults.baseURL = import.meta.env.VITE_API_FGTS_URL
  }

  const { data: appConfig } = useQuery({
    queryKey: ['AppConfig', import.meta.env.VITE_API_URL, whitelabel],
    queryFn: () => getAppConfig(),
    // select(data) {
    //   return {
    //     ...data,
    //     // customers: true,
    //     // unico: false,
    //   };
    // },
  })

  useEffect(() => {
    if (showCodeScreen && whitelabel) {
      if (whitelabel.url !== httpClient.defaults.baseURL) {
        httpClient.defaults.baseURL = whitelabel.url
        apiFGTS.defaults.baseURL = whitelabel.url
      }
    }
  }, [whitelabel])

  return (
    <WhitelabelContext.Provider
      value={{ login, logout, whitelabel, appConfig, showCodeScreen }}
    >
      {children}
    </WhitelabelContext.Provider>
  )
}

export const useWhitelabelContext = () => {
  const context = useContext(WhitelabelContext)
  if (!context) {
    throw new Error(
      'useWhitelabelContext deve ser usado dentro de um WhitelabelProvider',
    )
  }
  return context
}
